import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../utils/axios-auth';
import { getEffectiveUserId } from '../utils/auth-session-utils';
import { getUserFacingMessageFromAxiosError } from '../utils/user-facing-api-error';
/**
 * Fetches analytics data for a specific student with time frame filtering
 * Uses: GET /api/analytics/student/:studentId/timeframe/:timeFrame (week | month | quarter | year)
 * Optional query: classroom_id
 */
export const fetchStudentAnalytics = createAsyncThunk('analytics/fetchStudentAnalytics', async ({ studentId, timeFrame = 'week', classroomId }, { rejectWithValue }) => {
    try {
        const tf = timeFrame && ['week', 'month', 'quarter', 'year'].includes(timeFrame) ? timeFrame : 'week';
        const params = new URLSearchParams();
        if (classroomId)
            params.append('classroom_id', classroomId);
        const qs = params.toString();
        const url = `/analytics/student/${studentId}/timeframe/${tf}${qs ? `?${qs}` : ''}`;
        const response = await axiosInstance.get(url);
        // Process API response - backend wraps data in success/data structure
        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        // Fallback if response structure is different
        return response.data;
    }
    catch (error) {
        console.error('Error fetching student analytics:', error);
        // Check for unauthorized error (403)
        if (error.response?.status === 403) {
            return rejectWithValue('Permission denied: You do not have access to this student\'s analytics');
        }
        // Check if this is a network error - handle more gracefully
        const isNetworkError = error.code === 'ERR_NETWORK' ||
            error.message?.includes('Network Error') ||
            (!error.response && error.request);
        if (isNetworkError) {
            console.warn('Analytics API network error - connection unavailable:', error.message || 'Network Error');
            return rejectWithValue('Network unavailable. Please check your connection and try again.');
        }
        // Log response details for debugging
        if (error.response) {
            console.error('Response status:', error.response.status);
            console.error('Response data:', error.response.data);
        }
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to fetch student analytics' }));
    }
});
// Helper function to provide mock data for development
function getMockStudentAnalytics() {
    return {
        dates: ['2023-01-01', '2023-01-08', '2023-01-15', '2023-01-22', '2023-01-29'],
        scores: [65, 70, 75, 82, 85],
        subjects: ['Math', 'Science', 'English', 'History'],
        subject_scores: [75, 82, 68, 79],
        skills: ['Problem Solving', 'Critical Thinking', 'Communication', 'Research', 'Organization'],
        skill_levels: [4, 3, 4, 2, 5],
        achievements: ['Completed 5 lessons', 'Improved score by 20%', 'Mastered Algebra']
    };
}
/**
 * Fetches analytics data for a specific tutor with time frame filtering
 * Uses API endpoint: /api/analytics/tutor/:tutorId?timeFrame=:timeFrame
 */
export const fetchTutorAnalytics = createAsyncThunk('analytics/fetchTutorAnalytics', async ({ tutorId, timeFrame, studentId, classroomId, startDate, endDate }, { rejectWithValue }) => {
    try {
        const effectiveTutorId = getEffectiveUserId(tutorId) ?? tutorId;
        // Build URL with proper endpoint pattern: /api/analytics/tutor/:tutorId
        // Use URLSearchParams for proper query encoding
        const params = new URLSearchParams();
        params.append('timeFrame', timeFrame);
        if (studentId) {
            params.append('studentId', studentId);
        }
        if (classroomId) {
            params.append('classroomId', classroomId);
        }
        if (timeFrame === 'range' && startDate && endDate) {
            params.append('startDate', startDate);
            params.append('endDate', endDate);
        }
        const url = `/analytics/tutor/${effectiveTutorId}?${params.toString()}`;
        const response = await axiosInstance.get(url);
        // Process API response - backend wraps data in success/data structure
        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        // Fallback if response structure is different
        return response.data;
    }
    catch (error) {
        console.error('Error fetching tutor analytics:', error);
        // Check for unauthorized error (403)
        if (error.response?.status === 403) {
            return rejectWithValue('Permission denied: You do not have access to this tutor data');
        }
        // Log response details for debugging
        if (error.response) {
            console.error('Response status:', error.response.status);
            console.error('Response data:', error.response.data);
        }
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to fetch tutor analytics' }));
    }
});
// Helper function to provide mock tutor analytics data for development
function getMockTutorAnalytics() {
    return {
        dates: ['2023-01-01', '2023-01-08', '2023-01-15', '2023-01-22', '2023-01-29'],
        average_scores: [75, 78, 80, 83, 85],
        students: [
            { id: 'student1', name: 'Alex Johnson', average_score: 85 },
            { id: 'student2', name: 'Taylor Smith', average_score: 78 },
            { id: 'student3', name: 'Jamie Williams', average_score: 92 },
            { id: 'student4', name: 'Morgan Brown', average_score: 65 },
            { id: 'student5', name: 'Jordan Davis', average_score: 88 },
        ]
    };
}
/**
 * Fetches quiz analytics for a student
 * @route GET /api/analytics/student/:studentId/quiz
 */
export const fetchStudentQuizAnalytics = createAsyncThunk('analytics/fetchStudentQuizAnalytics', async ({ studentId, timeFrame = 'week', classroomId }, { rejectWithValue }) => {
    try {
        const params = new URLSearchParams();
        if (timeFrame)
            params.append('timeFrame', timeFrame);
        if (classroomId)
            params.append('classroomId', classroomId);
        const url = `/analytics/student/${studentId}/quiz?${params.toString()}`;
        const response = await axiosInstance.get(url);
        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        return response.data;
    }
    catch (error) {
        if (error.response?.status === 403) {
            return rejectWithValue('Permission denied: You do not have access to quiz analytics');
        }
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to fetch quiz analytics' }));
    }
});
/**
 * Fetches quiz analytics for a tutor
 * @route GET /api/analytics/tutor/:tutorId/quiz
 */
export const fetchTutorQuizAnalytics = createAsyncThunk('analytics/fetchTutorQuizAnalytics', async ({ tutorId, timeFrame = 'week', classroomId, quizId, studentId, startDate, endDate }, { rejectWithValue }) => {
    try {
        const params = new URLSearchParams();
        if (timeFrame)
            params.append('timeFrame', timeFrame);
        if (classroomId)
            params.append('classroomId', classroomId);
        if (quizId)
            params.append('quizId', quizId);
        if (studentId)
            params.append('studentId', studentId);
        if (timeFrame === 'range' && startDate)
            params.append('startDate', startDate);
        if (timeFrame === 'range' && endDate)
            params.append('endDate', endDate);
        const url = `/analytics/tutor/${tutorId}/quiz?${params.toString()}`;
        const response = await axiosInstance.get(url);
        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        return response.data;
    }
    catch (error) {
        if (error.response?.status === 403) {
            return rejectWithValue('Permission denied: You do not have access to quiz analytics');
        }
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to fetch quiz analytics' }));
    }
});
const initialState = {
    studentAnalytics: null,
    tutorAnalytics: null,
    studentQuizAnalytics: null,
    tutorQuizAnalytics: null,
    loading: false,
    error: null,
    unauthorized: false,
    quizAnalyticsLoading: false,
    quizAnalyticsError: null,
    feedbackAnalyticsLoading: false,
};
const analyticsSlice = createSlice({
    name: 'analytics',
    initialState,
    reducers: {
        clearAnalyticsError: (state) => {
            state.error = null;
            state.unauthorized = false;
        },
        clearAnalyticsState: (state) => {
            state.studentAnalytics = null;
            state.tutorAnalytics = null;
            state.studentQuizAnalytics = null;
            state.tutorQuizAnalytics = null;
            state.error = null;
            state.unauthorized = false;
            state.quizAnalyticsError = null;
            state.feedbackAnalyticsLoading = false;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchStudentAnalytics.pending, (state) => {
            state.loading = true;
            state.error = null;
            state.unauthorized = false;
        })
            .addCase(fetchStudentAnalytics.fulfilled, (state, action) => {
            state.loading = false;
            state.studentAnalytics = action.payload;
            state.error = null;
            state.unauthorized = false;
        })
            .addCase(fetchStudentAnalytics.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload ?? 'An error occurred';
            // Check if the error message indicates an authorization issue
            state.unauthorized = action.payload?.includes('Permission denied') ?? false;
        })
            .addCase(fetchTutorAnalytics.pending, (state) => {
            state.loading = true;
            state.error = null;
            state.unauthorized = false;
        })
            .addCase(fetchTutorAnalytics.fulfilled, (state, action) => {
            state.loading = false;
            state.tutorAnalytics = action.payload;
            state.error = null;
            state.unauthorized = false;
        })
            .addCase(fetchTutorAnalytics.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload ?? 'An error occurred';
            // Check if the error message indicates an authorization issue
            state.unauthorized = action.payload?.includes('Permission denied') ?? false;
        })
            .addCase(submitLessonFeedback.pending, (state) => {
            state.loading = true;
            state.error = null;
            state.unauthorized = false;
        })
            .addCase(submitLessonFeedback.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
            state.unauthorized = false;
        })
            .addCase(submitLessonFeedback.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload ?? 'An error occurred';
            state.unauthorized = action.payload?.includes('Permission denied') ?? false;
        })
            .addCase(fetchLessonFeedbackAnalytics.pending, (state) => {
            state.feedbackAnalyticsLoading = true;
            state.error = null;
            state.unauthorized = false;
        })
            .addCase(fetchLessonFeedbackAnalytics.fulfilled, (state) => {
            state.feedbackAnalyticsLoading = false;
            state.error = null;
            state.unauthorized = false;
        })
            .addCase(fetchLessonFeedbackAnalytics.rejected, (state, action) => {
            state.feedbackAnalyticsLoading = false;
            state.error = action.payload ?? 'An error occurred';
            state.unauthorized = action.payload?.includes('Permission denied') ?? false;
        })
            .addCase(fetchUnifiedFeedbackAnalytics.pending, (state) => {
            state.feedbackAnalyticsLoading = true;
            state.error = null;
            state.unauthorized = false;
        })
            .addCase(fetchUnifiedFeedbackAnalytics.fulfilled, (state) => {
            state.feedbackAnalyticsLoading = false;
            state.error = null;
            state.unauthorized = false;
        })
            .addCase(fetchUnifiedFeedbackAnalytics.rejected, (state, action) => {
            state.feedbackAnalyticsLoading = false;
            state.error = action.payload ?? 'An error occurred';
            state.unauthorized = action.payload?.includes('Permission denied') ?? false;
        })
            .addCase(fetchStudentQuizAnalytics.pending, (state) => {
            state.quizAnalyticsLoading = true;
            state.quizAnalyticsError = null;
        })
            .addCase(fetchStudentQuizAnalytics.fulfilled, (state, action) => {
            state.quizAnalyticsLoading = false;
            state.studentQuizAnalytics = action.payload;
            state.quizAnalyticsError = null;
        })
            .addCase(fetchStudentQuizAnalytics.rejected, (state, action) => {
            state.quizAnalyticsLoading = false;
            state.quizAnalyticsError = action.payload ?? 'An error occurred';
        })
            .addCase(fetchTutorQuizAnalytics.pending, (state) => {
            state.quizAnalyticsLoading = true;
            state.quizAnalyticsError = null;
        })
            .addCase(fetchTutorQuizAnalytics.fulfilled, (state, action) => {
            state.quizAnalyticsLoading = false;
            state.tutorQuizAnalytics = action.payload;
            state.quizAnalyticsError = null;
        })
            .addCase(fetchTutorQuizAnalytics.rejected, (state, action) => {
            state.quizAnalyticsLoading = false;
            state.quizAnalyticsError = action.payload ?? 'An error occurred';
        });
    },
});
/**
 * Submit lesson feedback
 * @param {Object} feedbackData - Lesson feedback data
 * @returns {Promise<Object>} Created lesson feedback
 */
export const submitLessonFeedback = createAsyncThunk('analytics/submitLessonFeedback', async (feedbackData, { rejectWithValue }) => {
    try {
        // Convert camelCase to snake_case for API
        const apiPayload = {
            lesson_id: feedbackData.lessonId,
            tutor_id: feedbackData.tutorId,
            classroom_id: feedbackData.classroomId,
            rating: feedbackData.rating,
            feedback_text: feedbackData.feedbackText,
            completion_time: feedbackData.completionTime,
            difficulty_rating: feedbackData.difficultyRating,
            engagement_level: feedbackData.engagementLevel,
            understanding_level: feedbackData.understandingLevel,
            would_recommend: feedbackData.wouldRecommend,
            metadata: feedbackData.metadata || {}
        };
        // Use /api/lesson-feedback endpoint (axiosInstance has baseURL /api)
        const response = await axiosInstance.post('/lesson-feedback', apiPayload);
        // Process API response
        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        return response.data;
    }
    catch (error) {
        console.error('Error submitting lesson feedback:', error);
        if (error.response?.status === 403) {
            return rejectWithValue('Permission denied: You do not have access to submit feedback for this lesson');
        }
        // Log response details for debugging
        if (error.response) {
            console.error('Response status:', error.response.status);
            console.error('Response data:', error.response.data);
        }
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to submit lesson feedback' }));
    }
});
/**
 * Get lesson feedback analytics for a tutor
 * @param {string} tutorId - Tutor ID
 * @param {string} classroomId - Optional classroom ID
 * @returns {Promise<Object>} Lesson feedback analytics
 */
export const fetchLessonFeedbackAnalytics = createAsyncThunk('analytics/fetchLessonFeedbackAnalytics', async ({ tutorId, classroomId }, { rejectWithValue }) => {
    try {
        // Build URL with proper query parameters
        let url = `/lesson-feedback/analytics/tutor/${tutorId}`;
        if (classroomId) {
            const params = new URLSearchParams();
            params.append('classroomId', classroomId);
            url += `?${params.toString()}`;
        }
        const response = await axiosInstance.get(url);
        // Process API response
        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        return response.data;
    }
    catch (error) {
        console.error('Error fetching lesson feedback analytics:', error);
        if (error.response?.status === 403) {
            return rejectWithValue('Permission denied: You do not have access to this tutor analytics');
        }
        // Log response details for debugging
        if (error.response) {
            console.error('Response status:', error.response.status);
            console.error('Response data:', error.response.data);
        }
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to fetch lesson feedback analytics' }));
    }
});
/**
 * Get unified feedback analytics (lesson + quiz feedback)
 * @param {string} tutorId - Tutor ID
 * @param {string} classroomId - Optional classroom ID
 * @returns {Promise<Object>} Unified feedback analytics
 */
export const fetchUnifiedFeedbackAnalytics = createAsyncThunk('analytics/fetchUnifiedFeedbackAnalytics', async ({ tutorId, classroomId }, { rejectWithValue }) => {
    try {
        // Build URL with proper query parameters
        let url = `/lesson-feedback/analytics/unified/tutor/${tutorId}`;
        if (classroomId) {
            const params = new URLSearchParams();
            params.append('classroomId', classroomId);
            url += `?${params.toString()}`;
        }
        const response = await axiosInstance.get(url);
        // Process API response
        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        return response.data;
    }
    catch (error) {
        console.error('Error fetching unified feedback analytics:', error);
        if (error.response?.status === 403) {
            return rejectWithValue('Permission denied: You do not have access to this tutor analytics');
        }
        // Log response details for debugging
        if (error.response) {
            console.error('Response status:', error.response.status);
            console.error('Response data:', error.response.data);
        }
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'Failed to fetch unified feedback analytics' }));
    }
});
export const { clearAnalyticsError, clearAnalyticsState } = analyticsSlice.actions;
export default analyticsSlice.reducer;
